import { Component, inject, Inject } from '@angular/core';
import { PagedAndSortedRequestDto } from '@shared/components/paged-listing-component-base';
import { TrainingZoneTemplateDto, ZoneDto } from '@core/services/training-zone/training-zone-template-dto';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppConstants } from '@core/constants';
import { SportService } from '@core/services/sports/sport.service';
import { SportPagedResultDto } from '@core/services/sports/sport-paged-result-dto';
import { SportDto } from '@core/services/sports/sport-dto';
import { TrainingZoneService } from '@core/services/training-zone/training-zone.service';
import { AppBaseComponent } from '@shared/components/app-component-base';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ValidatorHelper } from '@shared/helpers/ValidatorHelper';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog';
import * as _ from 'lodash-es';
import { ErrorFormFormatPipe } from '@shared/pipes/error-form-format.pipe';
import { SnackbarComponent } from '@shared/snackbar/snackbar.component';
import { FeatureManagementService } from '@shared/services/feature-management/feature-management.service';
import { FeatureFlagValue } from '@shared/services/feature-flags/interfaces/feature-flags.interface';
import { WaffleFeatureFlagsService } from '@shared/services/feature-flags/waffle-feature-flags.service';
import { FeatureFlagEnum } from '@shared/enums/feature-flag.enum';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AdditionalOutputMetricsDialogComponent } from '@shared/dialogs/additional-output-metrics-dialog/additional-output-metrics-dialog.component';
import { HelperService } from '@shared/services/helper.service';

class PagedTrainingZoneRequestDto extends PagedAndSortedRequestDto {
  public title: string | undefined;
}

@Component({
  selector: 'app-template-builder',
  templateUrl: './template-builder-dialog.component.html',
  styleUrls: ['./template-builder-dialog.component.scss'],
})
export class TemplateBuilderDialogComponent extends AppBaseComponent {
  public featureFlagsService: WaffleFeatureFlagsService = inject(WaffleFeatureFlagsService);
  public helperService: HelperService = inject(HelperService);

  public request: PagedTrainingZoneRequestDto = new PagedTrainingZoneRequestDto();
  public events: TrainingZoneTemplateDto[] = [];
  public sports: SportDto[] = [];
  public addForm: UntypedFormGroup = this.fb.group({});
  public referenceSystems: any[] = [];
  public masterMetrics: any[] = [];
  public filterMasterMetrics: any[] = [];
  public filterAdditionalMetrics: any[] = [];
  public germanFederationMasterMetrics: any[] = [];
  public targetValues: any[] = [];
  public addValues: any[] = [];
  public addValuesForSpeed: any[] = [];
  public addValuesForPower: any[] = [];
  public trainingZoneTemplate: TrainingZoneTemplateDto | undefined;
  public isLoaded: Promise<boolean> | undefined;
  public currentMetric: any = null;
  public currentAddValue: any = null;
  public maxValueOfValueField: number = 99999;
  public maxPercentage: number = 300;
  public isGermanFederation: FeatureFlagValue = this.featureFlagsService.getValue(FeatureFlagEnum.GERMAN_FEDERATION);
  public defaultZoneColors: string[] = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
    '#F8C471',
    '#82E0AA',
    '#F1948A',
    '#85C1E9',
    '#F4D03F',
    '#D7BDE2',
    '#A3E4D7',
    '#FAD7A0',
    '#D5A6BD',
    '#AED6F1',
  ];
  public isSpeedBasedSport: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<TemplateBuilderDialogComponent>,
    private sportService: SportService,
    private trainingZoneService: TrainingZoneService,
    private snackBar: MatSnackBar,
    private errorFormFormat: ErrorFormFormatPipe,
    private featureManagementService: FeatureManagementService,
    private flagManagementService: WaffleFeatureFlagsService,
    @Inject(MAT_DIALOG_DATA) private data: any
  ) {
    super();
  }

  public onInitPage(): void {
    this.initReferenceSystemData();
    this.initMasterMetric();
    this.initFeatureMasterMetrics();
    this.initGermanFederationMasterMetrics();
    this.sortMetrics();
    this.initTargetValue();
    this.initAddValue();
    this.loadSport();
    this.initForm();
  }

  public identifySportType(): void {
    const selectedSport = this.addForm.get('sport')?.value;
    this.isSpeedBasedSport = selectedSport.simulation_type === 1;
  }

  public onZoneDrop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      if (this.isDragDisabled()) {
        return;
      }

      const zonesArray = this.zones;
      const zones = zonesArray.controls;
      const movedControl = zones[event.previousIndex];
      zones.splice(event.previousIndex, 1);
      zones.splice(event.currentIndex, 0, movedControl);
      zonesArray.updateValueAndValidity();
      this.updateZoneNamesAfterReorder();
      this.announceDragDrop(event);
    }
  }

  public isDragDisabled(): boolean {
    return this.addForm.disabled || this.zones.length <= 1;
  }

  public getDragData(index: number): any {
    return {
      index: index,
      name: this.zones.at(index).get('name')?.value || `Zone ${index + 1}`,
      total: this.zones.length,
    };
  }

  public announceDragDrop(event: CdkDragDrop<any[]>): void {
    const fromIndex = event.previousIndex + 1;
    const toIndex = event.currentIndex + 1;
    console.log(`Moved zone from position ${fromIndex} to position ${toIndex}`);
  }

  private updateZoneNamesAfterReorder(): void {
    this.zones.controls.forEach((zoneControl, index) => {
      const currentName = zoneControl.get('name')?.value || '';

      if (currentName === '' || currentName.match(/^Zone \d+$/i)) {
        zoneControl.get('name')?.setValue(`Zone ${index + 1}`);
      }
    });
  }

  public onDragStarted(): void {
    document.body.classList.add('zone-dragging');
  }

  public onDragEnded(): void {
    document.body.classList.remove('zone-dragging');
  }

  public closeDialog(result: boolean): void {
    this.dialogRef.close(result);
  }

  public getFilteredMasterMetricsForFirst(zoneIndex: number): any[] {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const isDoubleMasterMetric = zoneGroup.get('is_double_master_metric')?.value;
    const selectedMetricAdditional = zoneGroup.get('master_metric_additional')?.value;
    const sourceList = isDoubleMasterMetric ? this.germanFederationMasterMetrics : this.filterMasterMetrics;

    return sourceList.filter((metric) => !selectedMetricAdditional || metric.value !== selectedMetricAdditional?.value);
  }

  public getDeltaMetrics(zoneIndex: number): any[] {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const selectedMetric1 = zoneGroup.get('master_metric')?.value;
    const deltaMetrics = this.filterMasterMetrics.filter(
      (metric) => AppConstants.DELTA_METRICS.includes(metric.value) && selectedMetric1.value !== metric.value
    );
    return deltaMetrics;
  }

  public getFilteredMasterMetricsForSecond(zoneIndex: number): any[] {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const selectedMetric1 = zoneGroup.get('master_metric')?.value;

    return this.germanFederationMasterMetrics.filter(
      (metric) => !selectedMetric1 || metric.value !== selectedMetric1?.value
    );
  }

  public onMasterMetricToggleChange(event: any, zoneIndex: number): void {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const isDoubleMasterMetric = event.checked;

    zoneGroup.get('is_double_master_metric')?.setValue(isDoubleMasterMetric);

    if (isDoubleMasterMetric) {
      zoneGroup.get('master_metric')?.setValue('');
      zoneGroup.get('master_metric_additional')?.setValue('');
      this.currentMetric = null;
      this.checkAndToggleEnergySpeed(zoneIndex);
    } else {
      zoneGroup.get('master_metric')?.setValue('');
      zoneGroup.get('master_metric_additional')?.setValue('');
      zoneGroup.get('is_speed_toggle')?.setValue(false);
      this.currentMetric = null;
    }

    this.setupConditionalValidatorsForZone(zoneGroup);
  }

  private checkAndToggleEnergySpeed(zoneIndex: number): void {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const referenceSystem = zoneGroup.get('referenceSystem')?.value;

    if (referenceSystem && referenceSystem.length > 0) {
      const speedPaceReferenceSystems = [
        AppConstants.REFERENCE_SYSTEM.SPEED_MS,
        AppConstants.REFERENCE_SYSTEM.SPEED_KM_H,
        AppConstants.REFERENCE_SYSTEM.SPEED_MP_H,
        AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_KM,
        AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_MILE,
        AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_100M,
        AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_500M,
      ];

      const hasSpeedOrPace = referenceSystem.some((ref: number) => speedPaceReferenceSystems.includes(ref));

      zoneGroup.get('is_speed_toggle')?.setValue(hasSpeedOrPace);
    } else {
      zoneGroup.get('is_speed_toggle')?.setValue(false);
    }
  }

  public onEnergySpeedToggleChange(event: any, zoneIndex: number): void {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    zoneGroup.get('is_speed_toggle')?.setValue(event.checked);
  }

  private setupConditionalValidatorsForZone(zoneGroup: UntypedFormGroup): void {
    const isDoubleMasterMetric = zoneGroup.get('is_double_master_metric')?.value;
    const masterMetricControl = zoneGroup.get('master_metric');
    const masterMetricAdditionalControl = zoneGroup.get('master_metric_additional');

    masterMetricControl?.setValidators([Validators.required]);

    if (isDoubleMasterMetric) {
      masterMetricAdditionalControl?.setValidators([Validators.required]);
    } else {
      masterMetricAdditionalControl?.clearValidators();
      masterMetricAdditionalControl?.setValue('');
    }

    masterMetricControl?.updateValueAndValidity();
    masterMetricAdditionalControl?.updateValueAndValidity();
  }

  public getFilteredMasterMetrics1(zoneIndex: number): any[] {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const selectedMetric2 = zoneGroup.get('masterMetric2')?.value;
    return this.germanFederationMasterMetrics.filter(
      (metric) => !selectedMetric2 || metric.value !== selectedMetric2.value
    );
  }

  public getFilteredMasterMetrics2(zoneIndex: number): any[] {
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    const selectedMetric1 = zoneGroup.get('masterMetric1')?.value;
    return this.germanFederationMasterMetrics.filter(
      (metric) => !selectedMetric1 || metric.value !== selectedMetric1.value
    );
  }

  public onSelectChangeFirstMasterMetric(id: any, i: number): void {
    const zoneGroup = this.zones.at(i) as UntypedFormGroup;
    const selectedMetric = this.germanFederationMasterMetrics.find((m) => m.value === id);
    zoneGroup.get('masterMetric1')?.setValue(selectedMetric);
  }

  public initReferenceSystemData(): void {
    this.referenceSystems = [
      { value: AppConstants.REFERENCE_SYSTEM.POWER_WATT, viewValue: 'Power (watt)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.POWER_WATT_KG, viewValue: 'Power (watt/kg)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_MS, viewValue: 'Speed (m/s)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_KM_H, viewValue: 'Speed (km/h)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_MP_H, viewValue: 'Speed (mp/h)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_KM, viewValue: 'Pace (min:sec/km)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_MILE, viewValue: 'Pace (min:sec/mile)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_100M, viewValue: 'Pace (min:sec/100m)', active: false },
      { value: AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_500M, viewValue: 'Pace (min:sec/500m)', active: false },
    ];
  }

  public openAdditionalMetricsDialog(zoneIndex: number): void {
    const zoneControl = this.zones.at(zoneIndex);
    const currentValues: number[] = zoneControl.get('outputValue')?.value || [];

    const dialogRef: MatDialogRef<AdditionalOutputMetricsDialogComponent> = this.dialog.open(
      AdditionalOutputMetricsDialogComponent,
      {
        width: '80vw',
        maxWidth: '80vw',
        maxHeight: '100vw',
        panelClass: ['general-dialog', 'additional-metrics'],
        disableClose: false,
        data: {
          availableMetrics: this.filterAdditionalMetrics.filter((item: any) => item.category),
          selectedValues: currentValues,
        },
      }
    );

    dialogRef.afterClosed().subscribe((result: number[] | null): void => {
      if (result !== null) {
        zoneControl.get('outputValue')?.setValue(result);
        zoneControl.get('outputValue')?.updateValueAndValidity();
      }
    });
  }

  private initMasterMetric(): void {
    this.masterMetrics = [
      // Heart rate
      {
        value: AppConstants.MASTER_METRIC.MAX_HEART_RATE_PERCENT,
        viewValue: 'Max heart rate (%)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'heart_rate',
      },
      {
        value: AppConstants.MASTER_METRIC.HEART_RATE,
        viewValue: 'Heart rate (bpm)',
        unit: 'bpm',
        additional: false,
        output: true,
        master: true,
        category: 'heart_rate',
      },

      // Performance Benchmarks
      {
        value: AppConstants.MASTER_METRIC.MAX_LACTATE_CLEARANCE,
        viewValue: 'Max. Lactate clearance (% of the mmol/l/min value of the lack of pyruvate)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },
      {
        value: AppConstants.MASTER_METRIC.ANAEROBIC_THRESHOLD,
        viewValue: 'Anaerobic Threshold (% of the speed /power at Anaerobic threshold)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },
      {
        value: AppConstants.MASTER_METRIC.VLA_MAX,
        viewValue: 'VLamax (%)',
        unit: '%',
        additional: true,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },
      {
        value: AppConstants.MASTER_METRIC.VO2_MAX,
        viewValue: 'VO2max (%)',
        unit: '%',
        additional: true,
        output: true,
        master: false,
        category: 'performance_benchmarks',
      },
      {
        value: AppConstants.MASTER_METRIC.VO2MAX_POWER,
        viewValue: 'VO2max-power (%)',
        unit: '%',
        additional: true,
        output: false,
        master: true,
        category: 'performance_benchmarks',
      },
      {
        value: AppConstants.MASTER_METRIC.FAT_MAX,
        viewValue: 'FatMax (% of the kcal/h at FatMax)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },
      {
        value: AppConstants.MASTER_METRIC.FAT_MAX_POWER,
        viewValue: 'FatMax power (%)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },

      // Energy Contribution
      {
        value: AppConstants.MASTER_METRIC.AEROBIC_POWER_KCAL_H,
        viewValue: 'Aerobic Power (kcal/h)',
        unit: 'kcal/h',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.AEROBIC_POWER_WATT,
        viewValue: 'Aerobic Power (Watt)',
        unit: 'Watt',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.AEROBIC_POWER_WATT_KG,
        viewValue: 'Aerobic Power (Watt/kg)',
        unit: 'Watt/kg',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.AEROBIC_POWER_JOULE_H,
        viewValue: 'Aerobic Power (kJ/h)',
        unit: 'kJ/h',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.ANAEROBIC_POWER_K_CAL,
        viewValue: 'Anaerobic Power (kcal/h)',
        unit: 'kcal/h',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT,
        viewValue: 'Anaerobic Power (Watt)',
        unit: 'Watt',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT_KG,
        viewValue: 'Anaerobic Power (Watt/kg)',
        unit: 'Watt/kg',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT_JOULE,
        viewValue: 'Anaerobic Power (kJ/h)',
        unit: 'kJ/h',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.ENERGY_CONTRIBUTION_AEROBIC,
        viewValue: 'Energy contribution aerobic (%)',
        unit: '%',
        output: true,
        master: true,
        category: 'energy_contribution',
      },
      {
        value: AppConstants.MASTER_METRIC.ENERGY_CONTRIBUTION_ANAEROBIC,
        viewValue: 'Energy contribution anaerobic (%)',
        unit: '%',
        additional: true,
        output: true,
        master: true,
        category: 'energy_contribution',
      },

      // Substrate Utilization
      {
        value: AppConstants.MASTER_METRIC.CARBOHYDRATES_PERCENT,
        viewValue: 'Carbohydrates (%)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },
      {
        value: AppConstants.MASTER_METRIC.CARBOHYDRATES_KCAL_H,
        viewValue: 'Carbohydrates (kcal/h)',
        unit: 'kcal/h',
        additional: false,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },
      {
        value: AppConstants.MASTER_METRIC.CARBOHYDRATES_G_H,
        viewValue: 'Carbohydrates (g/h)',
        unit: 'g/h',
        additional: false,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },
      {
        value: AppConstants.MASTER_METRIC.FAT_PERCENT,
        viewValue: 'Fat (%)',
        unit: '%',
        additional: false,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },
      {
        value: AppConstants.MASTER_METRIC.FAT_KCAL,
        viewValue: 'Fat (kcal/h)',
        unit: 'kcal/h',
        additional: false,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },
      {
        value: AppConstants.MASTER_METRIC.FAT_GH,
        viewValue: 'Fat (g/h)',
        unit: 'g/h',
        additional: false,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },
      {
        value: AppConstants.MASTER_METRIC.TOTAL_ENERGY,
        viewValue: 'Total Energy (kcal/h)',
        unit: 'kcal/h',
        additional: true,
        output: true,
        master: true,
        category: 'substrate_utilization',
      },

      // Lactate
      {
        value: AppConstants.MASTER_METRIC.LACTATE_ACCUMULATION_RATE,
        viewValue: 'Lactate accumulation rate (mmol/l/min)',
        unit: 'mmol/l/min',
        additional: true,
        output: true,
        master: true,
        category: 'lactate',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CLEARANCE_RATE,
        viewValue: 'Lactate clearance rate (mmol/l/min)',
        unit: 'mmol/l/min',
        additional: true,
        output: true,
        master: true,
        category: 'lactate',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION,
        viewValue: 'Lactate concentration (mmol/l)',
        unit: 'mmol/l',
        additional: true,
        output: true,
        master: true,
        category: 'lactate',
      },
      {
        value: AppConstants.MASTER_METRIC.MAX_LACTATE_PRODUCTION_RATE,
        viewValue: 'Lactate production rate (mmol/l/min)',
        unit: 'mmol/l/min',
        additional: false,
        output: true,
        master: true,
        category: 'lactate',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_SHUTTLING,
        viewValue: 'Lactate shuttling',
        unit: '',
        additional: false,
        output: false,
        master: true,
        category: 'lactate',
      },
    ];
    this.filterAdditionalMetrics = this.masterMetrics.filter((item) => item.output);
    this.filterMasterMetrics = this.masterMetrics.filter((item) => item.master);
  }

  private initFeatureMasterMetrics(): void {
    const featureMetrics: Record<string, Record<string, any>> = {
      lt_1: {
        value: AppConstants.MASTER_METRIC.LT1,
        viewValue: 'LT1 (%)',
        unit: '%',
        additional: true,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },
      min_lactate_eq: {
        value: AppConstants.MASTER_METRIC.MINIMUM_LACTATE_EQUIVALENT,
        viewValue: 'Minimum Lactate Equivalent (mmol/l/min)',
        unit: 'mmol/l/min',
        additional: true,
        output: true,
        master: true,
        category: 'lactate',
      },
      max_power: {
        value: AppConstants.MASTER_METRIC.MAX_POWER,
        viewValue: 'Maximum Power (Watt)',
        unit: 'Watt',
        additional: true,
        output: false,
        master: true,
        category: 'power',
      },
      max_aerobic: {
        value: AppConstants.MASTER_METRIC.MAX_AEROBIC,
        viewValue: 'MAS',
        unit: '',
        additional: true,
        output: true,
        master: true,
        category: 'performance_benchmarks',
      },
      anaerobic_reserve: {
        value: AppConstants.MASTER_METRIC.ANAEROBIC_RESERVE,
        viewValue: 'ASR',
        unit: '',
        additional: true,
        output: true,
        master: true,
      },
      mmss: {
        value: AppConstants.MASTER_METRIC.MMSS,
        viewValue: 'Maximum Metabolic Steady State (Watt)',
        unit: 'Watt',
        additional: true,
        output: true,
        master: true,
        category: 'mmss',
      },
    };
    for (const slug in featureMetrics) {
      if (this.featureManagementService.getFeatureValue(slug)) {
        this.masterMetrics.push(featureMetrics[slug]);
      }
    }

    const initialMasterMetric: any[] = this.helperService.makeCopy(this.masterMetrics);
    if (this.flagManagementService.getValue(FeatureFlagEnum.GERMAN_FEDERATION)) {
      const fixedLactateConcMetrics = [2, 4, 6, 8, 10].map((value) => ({
        value: AppConstants.MASTER_METRIC[`LACTATE_CONCENTRATION_${value}` as keyof typeof AppConstants.MASTER_METRIC],
        viewValue: `Fixed Lactate Concentration - ${value} (%)`,
        unit: '%',
        additional: true,
        output: true,
        master: true,
      }));
      this.masterMetrics = [...this.masterMetrics, ...fixedLactateConcMetrics];
    }

    this.filterAdditionalMetrics = initialMasterMetric.filter((item) => item.output);
    this.filterMasterMetrics = this.masterMetrics.filter((item) => item.master);
  }

  private sportMapMasterMetrics(): void {
    const selectedSport = this.addForm.get('sport')?.value;
    const simulationType = selectedSport.simulation_type;
    const isSpeed = simulationType === 1; // Power by default
    this.filterMasterMetrics = this.mapMetricsWithSportNaming(this.filterMasterMetrics, isSpeed);
    this.filterAdditionalMetrics = this.mapMetricsWithSportNaming(this.filterAdditionalMetrics, isSpeed);
  }

  private mapMetricsWithSportNaming(metrics: any[], isSpeed = false): any[] {
    return metrics.map((metric) => {
      if (isSpeed) {
        switch (metric.value) {
          case AppConstants.MASTER_METRIC.MAX_POWER: {
            metric.viewValue = 'Maximum Speed (m/s)';
            metric.unit = 'm/s';
            return metric;
          }
          case AppConstants.MASTER_METRIC.MAX_AEROBIC: {
            metric.viewValue = 'Maximum Aerobic Speed (m/s)';
            metric.unit = 'm/s';
            return metric;
          }
          // TODO
          // case AppConstants.MASTER_METRIC.ANAEROBIC_RESERVE: {
          //   metric.viewValue = "Anaerobic Speed Reserve (m/s)"
          //   metric.unit = "m/s"
          //   return metric
          // }
          default:
            return metric;
        }
      } else {
        switch (metric.value) {
          case AppConstants.MASTER_METRIC.MAX_POWER: {
            metric.viewValue = 'Maximum Power (Watt)';
            metric.unit = 'Watt';
            return metric;
          }
          case AppConstants.MASTER_METRIC.MAX_AEROBIC: {
            metric.viewValue = 'Maximum Aerobic Power (Watt)';
            metric.unit = 'Watt';
            return metric;
          }
          // TODO
          // case AppConstants.MASTER_METRIC.ANAEROBIC_RESERVE: {
          //   metric.viewValue = "Anaerobic Power Reserve (Watt)"
          //   metric.unit = "Watt"
          //   return metric
          // }
          default:
            return metric;
        }
      }
    });
  }

  private initGermanFederationMasterMetrics(): void {
    if (!this.isGermanFederation) return;

    this.germanFederationMasterMetrics = [
      { value: AppConstants.MASTER_METRIC.FAT_MAX, viewValue: 'FatMax', unit: '%' },
      { value: AppConstants.MASTER_METRIC.LT1, viewValue: 'LT1', unit: '%' },
      { value: AppConstants.MASTER_METRIC.CARB_MAX, viewValue: 'CarbMax', unit: '' },
      {
        value: AppConstants.MASTER_METRIC.MINIMUM_LACTATE_EQUIVALENT,
        viewValue: 'minimum Lactate Equivalent',
        unit: '',
      },
      {
        value: AppConstants.MASTER_METRIC.MAX_LACTATE_CLEARANCE,
        viewValue: 'Max. Lactate clearance rate',
        unit: 'mmol/l/min',
      },
      { value: AppConstants.MASTER_METRIC.VO2MAX_POWER, viewValue: 'VO2maxpower', unit: '%' },
      { value: AppConstants.MASTER_METRIC.MLSS, viewValue: 'MLSS', unit: '' },
      { value: AppConstants.MASTER_METRIC.MMSS, viewValue: 'MMSS', unit: '' },
      { value: AppConstants.MASTER_METRIC.MAS, viewValue: 'MAS (speed based sport)', unit: 'm/s' },
      { value: AppConstants.MASTER_METRIC.MAX_AEROBIC, viewValue: 'MAP (power based sport)', unit: 'W' },
      { value: AppConstants.MASTER_METRIC.MAX_POWER, viewValue: 'max. Power (power based sport)', unit: 'W' },
      { value: AppConstants.MASTER_METRIC.MAX_SPEED, viewValue: 'max Speed (speed based sport)', unit: 'm/s' },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION_2,
        viewValue: 'fixed Lactate concentration 2 mmol/l',
        unit: 'mmol/l',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION_4,
        viewValue: 'fixed Lactate concentration 4 mmol/l',
        unit: 'mmol/l',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION_6,
        viewValue: 'fixed Lactate concentration 6 mmol/l',
        unit: 'mmol/l',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION_8,
        viewValue: 'fixed Lactate concentration 8 mmol/l',
        unit: 'mmol/l',
      },
      {
        value: AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION_10,
        viewValue: 'fixed Lactate concentration 10 mmol/l',
        unit: 'mmol/l',
      },
    ].filter(
      (metric) =>
        metric.value !== AppConstants.MASTER_METRIC.LT1 || this.featureManagementService.getFeatureValue('lt_1')
    );
  }

  private sortMetrics(): void {
    this.filterMasterMetrics.sort((metricA, metricB) => this.stringComparator(metricA.viewValue, metricB.viewValue));
    this.filterAdditionalMetrics.sort((metricA, metricB) =>
      this.stringComparator(metricA.viewValue, metricB.viewValue)
    );
    if (this.germanFederationMasterMetrics) {
      this.germanFederationMasterMetrics.sort((metricA, metricB) =>
        this.stringComparator(metricA.viewValue, metricB.viewValue)
      );
    }
  }

  private stringComparator(string1: string, string2: string): number {
    return string1 < string2 ? -1 : string1 > string2 ? 1 : 0;
  }

  public initTargetValue(): void {
    this.targetValues = [
      { value: 1, viewValue: '% - percentage' },
      { value: 2, viewValue: '- fixed value' },
    ];
  }

  private initAddValue(): void {
    this.addValuesForSpeed = [
      { value: 0, viewValue: 'None', unit: '' },
      { value: 1, viewValue: 'distance (m)', unit: 'm' },
      { value: 2, viewValue: 'time (s)', unit: 's' },
    ];
    this.addValuesForPower = [
      { value: 0, viewValue: 'None', unit: '' },
      { value: 2, viewValue: 'time (s)', unit: 's' },
    ];
  }

  private validateZoneColors(): boolean {
    return this.addForm.value.zones.every((zone: any) => {
      return zone.color && zone.color.trim() !== '';
    });
  }

  public onSubmit(): void {
    if (this.addForm.value.zones.length === 0) {
      this.snackBar.open('Zone is required', 'OK', AppConstants.TOAST_CONFIG.ERROR);
      return;
    }

    if (!this.validateZoneColors()) {
      this.snackBar.open('Zone color is required for all zones', 'OK', AppConstants.TOAST_CONFIG.ERROR);
      return;
    }

    if (
      !this.checkOutputValue() ||
      !this.checkSystemRefValue() ||
      !this.checkAdditionalValue() ||
      !this.checkMaxHeartRatePercent() ||
      this.isInvalidAddValueValue()
    ) {
      this.showValidationErrors();
      return;
    }

    if (this.addForm.status === 'INVALID') {
      this.showErrorWhenInvalidForm();
      return;
    }

    if (this.auth?.role?.is_owner && this.addForm.value['isDefault']) {
      this.confirmDefaultTemplate();
    } else {
      this.handleSubmitData();
    }
  }

  private showValidationErrors(): void {
    if (!this.checkOutputValue()) {
      this.snackBar.open(
        'Maximum output value is ' + AppConstants.TRAINING_ZONE_VALIDATION.MAX_OUTPUT,
        'OK',
        AppConstants.TOAST_CONFIG.ERROR
      );
    } else if (!this.checkSystemRefValue()) {
      this.snackBar.open(
        'Maximum system reference is ' + AppConstants.TRAINING_ZONE_VALIDATION.MAX_SYSTEM_REF,
        'OK',
        AppConstants.TOAST_CONFIG.ERROR
      );
    } else if (!this.checkAdditionalValue()) {
      this.snackBar.open('Additional value is required', 'OK', AppConstants.TOAST_CONFIG.ERROR);
    } else if (!this.checkMaxHeartRatePercent()) {
      this.snackBar.open(
        'Target, upper, lower of max heart rate percent must be from 20% to 100%',
        'OK',
        AppConstants.TOAST_CONFIG.ERROR
      );
    } else if (this.isInvalidAddValueValue()) {
      this.snackBar.open(
        'The value must be less than or equal ' + this.maxValueOfValueField,
        'OK',
        AppConstants.TOAST_CONFIG.ERROR
      );
    }
  }

  private confirmDefaultTemplate(): void {
    let dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      disableClose: false,
    });
    dialogRef.componentInstance.confirmMessage = 'Are you sure you want to set this as default the template?';
    dialogRef.afterClosed().subscribe((result): void => {
      if (result) {
        this.handleSubmitData();
      } else {
        this.addForm?.patchValue({ isDefault: false });
        this.addForm.get('isPublic')?.enable();
      }
    });
  }

  private showErrorWhenInvalidForm(): void {
    let error_messages: Array<string> = [];
    let error_object: Array<any> = [];
    const zones: AbstractControl<any, any>[] = (this.addForm.get('zones') as UntypedFormArray).controls;
    _.forEach(zones, (zone: AbstractControl<any, any>): void => {
      const values: AbstractControl<any, any> = (zone.get('values') as UntypedFormArray).controls[0];
      if (error_messages.indexOf('Target') === -1 && values.get('targetValue')?.errors) {
        error_object.push(this.errorFormFormat.transform(values.get('targetValue')?.errors, { fieldName: 'Target' }));
        error_messages.push('Target');
      }
      if (error_messages.indexOf('Lower') === -1 && values.get('lowerValue')?.errors) {
        error_object.push(this.errorFormFormat.transform(values.get('lowerValue')?.errors, { fieldName: 'Lower' }));
        error_messages.push('Lower');
      }
      if (error_messages.indexOf('Upper') === -1 && values.get('upperValue')?.errors) {
        error_object.push(this.errorFormFormat.transform(values.get('upperValue')?.errors, { fieldName: 'Upper' }));
        error_messages.push('Upper');
      }
      if (error_messages.indexOf('Value') === -1 && values.get('addValueValue')?.errors) {
        error_object.push(this.errorFormFormat.transform(values.get('addValueValue')?.errors, { fieldName: 'Value' }));
        error_messages.push('Value');
      }
    });

    if (error_object.length > 0) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        panelClass: 'error',
        verticalPosition: 'bottom',
        data: { html: error_object.join('<br/>') },
      });
    } else {
      this.snackBar.open('Please full fill the form', 'OK', AppConstants.TOAST_CONFIG.ERROR);
    }
  }

  private isInvalidAddValueValue(): boolean {
    const zoneControl: AbstractControl<any, any>[] = (this.addForm.controls.zones as UntypedFormArray).controls;
    return zoneControl.some((item: any) => item?.controls?.values?.controls[0].controls.addValueValue.errors?.max);
  }

  public handleSubmitData(): void {
    this.addForm.value['is_default'] = !!this.addForm.value['isDefault'];
    this.addForm.value['is_public'] = !!this.addForm.value['isPublic'];
    this.addForm.value['sport_id'] = this.addForm.value['sport'].id;
    if (this.addForm.value['is_default']) {
      this.addForm.value['is_public'] = true;
    }

    this.addForm.value.zones.map((item: any, index: number) => {
      item['index'] = index + 1; // Ensure correct order after drag/drop
      item['reference_system'] = item.referenceSystem.join(',');
      item['master_metric'] = item.master_metric?.value?.toString() || '';
      item['color'] = item.color;

      if (this.isGermanFederation && item.is_double_master_metric && item.master_metric_additional) {
        item['master_metric_additional'] = item.master_metric_additional?.value?.toString() || '';
        item['is_speed_toggle'] = item.is_speed_toggle;
      }

      item['output_value'] = item.outputValue ? item.outputValue.join(',') : '';
      item['zone_input_respected_values'] =
        item.values?.map((value: any) => ({
          target: value.targetValue,
          target_unit: 0,
          lower: value.lowerValue,
          lower_unit: 0,
          upper: value.upperValue,
          upper_unit: 0,
          add_value: value.addValueValue || 0,
          add_value_unit: value.addValueSelect?.value || 0,
        })) || [];

      return item;
    });

    const serviceCall =
      !this.data.id || this.data.isCopy
        ? this.trainingZoneService.create(this.addForm.value)
        : this.trainingZoneService.update({ ...this.addForm.value, id: this.data.id });

    serviceCall.subscribe(
      () => this.closeDialog(true),
      (error) => {
        this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
        this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      }
    );
  }

  public checkMaxHeartRatePercent(): boolean {
    return this.addForm.value.zones.every((item: any) => {
      if (item.master_metric && item?.master_metric?.value === AppConstants.MASTER_METRIC.MAX_HEART_RATE_PERCENT) {
        if (item.values) {
          const values = item.values[0];
          return (
            this.isValidHeartRateValue(values.lowerValue) &&
            this.isValidHeartRateValue(values.upperValue) &&
            this.isValidHeartRateValue(values.targetValue)
          );
        }
      }
      return true;
    });
  }

  private isValidHeartRateValue(value: any): boolean {
    return !value || (value >= 20 && value <= 100);
  }

  public checkOutputValue(): boolean {
    return this.addForm.value.zones.every(
      (item: any) => !item.outputValue || item.outputValue.length <= AppConstants.TRAINING_ZONE_VALIDATION.MAX_OUTPUT
    );
  }

  public checkSystemRefValue(): boolean {
    return this.addForm.value.zones.every(
      (item: any) =>
        !item.referenceSystem || item.referenceSystem.length <= AppConstants.TRAINING_ZONE_VALIDATION.MAX_SYSTEM_REF
    );
  }

  public checkAdditionalValue(): boolean {
    return this.addForm.value.zones.every((item: any) => {
      if (item.values && item?.master_metric?.value != AppConstants.MASTER_METRIC.LACTATE_SHUTTLING) {
        return item.values.every((value: any) => value.addValueSelect || parseInt(value.addValueValue));
      }
      return true;
    });
  }

  public addNewZone(): void {
    if (this.addForm.value.zones.length >= AppConstants.TRAINING_ZONE_VALIDATION.MAX_ZONE) {
      this.snackBar.open('Cannot add new zone, maximum 20 zones', 'OK', AppConstants.TOAST_CONFIG.ERROR);
      return;
    }

    const zoneIndex = this.zones.length + 1;
    const zoneCode = this.zones.length + 1;

    const currentZoneIndex = this.addForm.value.zones.length;
    const defaultColor = this.defaultZoneColors[currentZoneIndex % this.defaultZoneColors.length];
    const baseZoneConfig = {
      name: [`Zone ${zoneIndex}`, Validators.required],
      code: [zoneCode, Validators.required],
      referenceSystem: ['', Validators.required],
      values: this.fb.array([
        this.fb.group({
          targetValue: ['', Validators.required],
          lowerValue: ['', Validators.required],
          upperValue: ['', Validators.required],
          addValueSelect: [''],
          addValueValue: [
            '',
            [Validators.max(this.maxValueOfValueField), Validators.pattern(AppConstants.REGEX.INTEGER)],
          ],
        }),
      ]),
      outputValue: [''],
      master_metric: ['', Validators.required],
      color: [defaultColor, Validators.required],
    };

    if (this.isGermanFederation) {
      Object.assign(baseZoneConfig, {
        is_double_master_metric: [false],
        master_metric_additional: [''],
        is_speed_toggle: [false],
      });
    }

    this.zones.push(this.fb.group(baseZoneConfig));
  }

  public onZoneColorChange(event: Event, zoneIndex: number): void {
    const target = event.target as HTMLInputElement;
    const color = target.value;
    const zoneGroup = this.zones.at(zoneIndex) as UntypedFormGroup;
    zoneGroup.get('color')?.setValue(color);
  }

  public get zones(): UntypedFormArray {
    return this.addForm.get('zones') as UntypedFormArray;
  }

  public getFormValues(i: number): UntypedFormArray {
    return (this.addForm.get('zones') as UntypedFormArray).controls[i].get('values') as UntypedFormArray;
  }

  public setFormValue(i: number): void {
    (this.addForm.get('zones') as UntypedFormArray).controls[i].patchValue({ referenceSystem: '' });
  }

  public setAdditionalValue(i: number): void {
    this.getFormValues(i).controls[0].patchValue({ addValueValue: 0 });
  }

  public setAdditionalSelect(i: number): void {
    this.getFormValues(i).controls[0].patchValue({ addValueSelect: this.addValues[0] });
  }

  public onSelectChangeMaterMetric(id: any, i: number): void {
    this.currentMetric = this.masterMetrics.find((item): boolean => item.value === id);

    const zone = (this.addForm.get('zones') as UntypedFormArray).controls[i];
    const values = (zone.get('values') as UntypedFormArray).controls[0];
    const output = zone.get('outputValue') as UntypedFormArray;

    if (id === this.constant.MASTER_METRIC.LACTATE_SHUTTLING) {
      this.disableValueFields(values, output);
    } else {
      this.enableValueFields(values, output);
    }
  }

  private disableValueFields(values: AbstractControl<any, any>, output: UntypedFormArray): void {
    ['lowerValue', 'upperValue', 'targetValue', 'addValueSelect', 'addValueValue'].forEach((field) => {
      const control = values.get(field);
      control?.clearValidators();
      control?.disable();
      control?.updateValueAndValidity();
    });
    output.clearValidators();
    output.disable();
    output.updateValueAndValidity();
  }

  private enableValueFields(values: AbstractControl<any, any>, output: UntypedFormArray): void {
    const validator_array: any[] = this.updateValidatorBasedOnMasterMetric();
    ['lowerValue', 'upperValue', 'targetValue'].forEach((field) => {
      const control = values.get(field);
      control?.setValidators(validator_array);
      control?.enable();
      control?.updateValueAndValidity();
    });
    ['addValueSelect', 'addValueValue'].forEach((field) => {
      const control = values.get(field);
      control?.enable();
      control?.updateValueAndValidity();
    });
    output.setValidators([Validators.required, ValidatorHelper.validateOutputValues]);
    output.enable();
    output.updateValueAndValidity();
  }

  public updateValidatorBasedOnMasterMetric(): Array<any> {
    const default_validator = [Validators.required];
    const integerMetrics = [
      AppConstants.MASTER_METRIC.AEROBIC_POWER_KCAL_H,
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_K_CAL,
      AppConstants.MASTER_METRIC.CARBOHYDRATES_KCAL_H,
      AppConstants.MASTER_METRIC.FAT_KCAL,
      AppConstants.MASTER_METRIC.TOTAL_ENERGY,
      AppConstants.MASTER_METRIC.CARBOHYDRATES_G_H,
      AppConstants.MASTER_METRIC.FAT_GH,
      AppConstants.MASTER_METRIC.AEROBIC_POWER_WATT,
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT,
      AppConstants.MASTER_METRIC.AEROBIC_POWER_JOULE_H,
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT_JOULE,
      AppConstants.MASTER_METRIC.HEART_RATE,
    ];
    const percentageMetrics = [
      AppConstants.MASTER_METRIC.ANAEROBIC_THRESHOLD,
      AppConstants.MASTER_METRIC.CARBOHYDRATES_PERCENT,
      AppConstants.MASTER_METRIC.ENERGY_CONTRIBUTION_AEROBIC,
      AppConstants.MASTER_METRIC.ENERGY_CONTRIBUTION_ANAEROBIC,
      AppConstants.MASTER_METRIC.FAT_PERCENT,
      AppConstants.MASTER_METRIC.FAT_MAX,
      AppConstants.MASTER_METRIC.FAT_MAX_POWER,
      AppConstants.MASTER_METRIC.MAX_HEART_RATE_PERCENT,
      AppConstants.MASTER_METRIC.MAX_LACTATE_CLEARANCE,
      AppConstants.MASTER_METRIC.VO2_MAX,
      AppConstants.MASTER_METRIC.VLA_MAX,
      AppConstants.MASTER_METRIC.VO2MAX_POWER,
    ];

    if (integerMetrics.includes(this.currentMetric.value)) {
      default_validator.push(Validators.pattern(AppConstants.REGEX.INTEGER));
    } else if (percentageMetrics.includes(this.currentMetric.value)) {
      default_validator.push(Validators.pattern(AppConstants.REGEX.DECIMAL_2), Validators.max(this.maxPercentage));
    } else {
      default_validator.push(Validators.pattern(AppConstants.REGEX.DECIMAL_2));
    }
    return default_validator;
  }

  public getCurrentMasterMetric(i: number) {
    const zone: AbstractControl<any, any> = (this.addForm.get('zones') as UntypedFormArray).controls[i];
    const masterMetricValue = zone.get('master_metric')?.value;

    // Handle both cases: when value is an object with .value property, or when it's a direct number
    const metricId = masterMetricValue?.value || masterMetricValue;

    if (!metricId) {
      return null;
    }

    // Check the main master metrics list
    return this.masterMetrics.find((item): boolean => item.value === metricId);
  }

  public getCurrentAddValue(i: number) {
    const zone: AbstractControl<any, any> = (this.addForm.get('zones') as UntypedFormArray).controls[i];
    const values: AbstractControl<any, any> = (zone.get('values') as UntypedFormArray).controls[0];
    const result = this.addValues.find(
      (item): boolean => item.value === parseInt(values?.value?.addValueSelect?.value)
    );
    return result?.unit == '' ? 'Unit' : result?.unit;
  }

  public isDisableValue(): boolean {
    return this.currentMetric === this.constant.MASTER_METRIC.LACTATE_SHUTTLING;
  }

  public onSelectChangeAddValue(id: any, i: number): void {
    this.currentAddValue = this.addValues.find((item): boolean => item.value === id);
    if (!this.currentAddValue.value) {
      this.setAdditionalValue(i);
    }
  }

  public onSelectSystemReferenceChange(event: any, i: number): void {
    const zoneGroup = this.zones.at(i) as UntypedFormGroup;

    if (this.isGermanFederation && zoneGroup.get('is_double_master_metric')?.value) {
      this.checkAndToggleEnergySpeed(i);
    }

    const maxSystemRef: number = AppConstants.TRAINING_ZONE_VALIDATION.MAX_SYSTEM_REF;
    if (event.value.length > maxSystemRef) {
      this.snackBar.open('Maximum system reference value is ' + maxSystemRef, 'OK', AppConstants.TOAST_CONFIG.ERROR);
    }

    this.updateAdditionalType(event.value);
    if (this.zones?.controls[i]?.value?.values[0] && !this.zones?.controls[i]?.value?.values[0]?.addValueSelect) {
      this.setAdditionalSelect(i);
    }
  }

  public copyZone(index: number): void {
    if (this.addForm.value.zones.length >= AppConstants.TRAINING_ZONE_VALIDATION.MAX_ZONE) {
      this.snackBar.open('Cannot copy zone, maximum 20 zones', 'OK', AppConstants.TOAST_CONFIG.ERROR);
      return;
    }

    const sourceZone: any = this.zones.at(index) as UntypedFormGroup;
    const sourceValue = sourceZone.value;
    const newZoneIndex = this.zones.length + 1;

    const newValues = this.fb.array(
      sourceValue.values.map((val: any) =>
        this.fb.group({
          targetValue: [val.targetValue, sourceZone.get('values')?.at(0)?.get('targetValue')?.validator],
          lowerValue: [val.lowerValue, sourceZone.get('values')?.at(0)?.get('lowerValue')?.validator],
          upperValue: [val.upperValue, sourceZone.get('values')?.at(0)?.get('upperValue')?.validator],
          addValueSelect: [val.addValueSelect],
          addValueValue: [val.addValueValue, sourceZone.get('values')?.at(0)?.get('addValueValue')?.validator],
        })
      )
    );

    const currentZoneIndex = this.zones.length;
    const defaultColor = this.defaultZoneColors[currentZoneIndex % this.defaultZoneColors.length];
    const baseZoneConfig: any = {
      name: [this.formatCopyName(sourceValue.name), Validators.required],
      code: [newZoneIndex, Validators.required],
      referenceSystem: [sourceValue.referenceSystem, sourceZone.get('referenceSystem')?.validator],
      values: newValues,
      outputValue: [sourceValue.outputValue, sourceZone.get('outputValue')?.validator],
      master_metric: [sourceValue.master_metric, Validators.required],
      color: [sourceValue.color || defaultColor, Validators.required],
    };

    if (this.isGermanFederation) {
      Object.assign(baseZoneConfig, {
        is_double_master_metric: [sourceValue.is_double_master_metric || false],
        master_metric_additional: [sourceValue.master_metric_additional || ''],
        is_speed_toggle: [sourceValue.is_speed_toggle || false],
      });
    }

    this.zones.insert(index + 1, this.fb.group(baseZoneConfig));
    this.updateZoneNamesAfterReorder();
  }

  public deleteZone(i: number): void {
    this.zones.removeAt(i);
    this.updateZoneNamesAfterReorder();
  }

  public updateAdditionalType(value: any): void {
    this.addValues =
      value.includes(AppConstants.REFERENCE_SYSTEM.POWER_WATT_KG) ||
      value.includes(AppConstants.REFERENCE_SYSTEM.POWER_WATT)
        ? this.addValuesForPower
        : this.addValuesForSpeed;
  }

  public onSelectSport(): void {
    this.zones.controls.forEach((item, i: number): void => {
      this.setFormValue(i);
    });
    this.sportMapMasterMetrics();
    this.identifySportType();
  }

  public onSelectOutputValue(event: any): void {
    const maxOutput: number = AppConstants.TRAINING_ZONE_VALIDATION.MAX_OUTPUT;
    if (event.value.length > maxOutput) {
      this.snackBar.open('Maximum output value is ' + maxOutput, 'OK', AppConstants.TOAST_CONFIG.ERROR);
    }
  }

  public isEditTemplate() {
    return this.data.isAllowEdit;
  }

  public getDragHandleClass(): string {
    return 'drag-handle';
  }

  private loadSport(): void {
    this.sportService.listAll().subscribe((result: SportPagedResultDto): void => {
      this.sports = result.items;
      if (this.data.id) {
        this.loadDetailData();
      } else {
        this.isLoaded = Promise.resolve(true);
      }
    });
  }

  private loadDetailData(): void {
    this.trainingZoneService.get(this.data.id).subscribe((result: TrainingZoneTemplateDto): void => {
      this.trainingZoneTemplate = result;
      this.initForm(result);
      this.sportMapMasterMetrics();
      this.isLoaded = Promise.resolve(true);
    });
  }

  private getReferenceSystem(value: number) {
    const item = this.referenceSystems.find((i): boolean => i.value === value);
    return item ? { id: item.value, name: item.viewValue } : null;
  }

  private initForm(data?: TrainingZoneTemplateDto): void {
    if (data) {
      this.onSelectSport();
      const zones = this.initZoneForm(data);
      this.addForm = this.fb.group({
        name: [!this.data.isCopy ? data.name : this.formatCopyName(data.name!), Validators.required],
        description: [data.description, Validators.required],
        sport: [data.sport.id, Validators.required],
        isDefault: [data.is_default],
        isPublic: [{ value: data.is_public, disabled: !!data.is_default }],
        zones: zones,
      });
      this.initValue(data);
      this.identifySportType();
    } else {
      const baseZoneConfig = {
        name: ['Zone 1', Validators.required],
        code: [1, Validators.required],
        referenceSystem: ['', Validators.required],
        values: this.fb.array([
          this.fb.group({
            targetValue: ['', [Validators.required, Validators.pattern(AppConstants.REGEX.DECIMAL_2)]],
            lowerValue: ['', [Validators.required, Validators.pattern(AppConstants.REGEX.DECIMAL_2)]],
            upperValue: ['', [Validators.required, Validators.pattern(AppConstants.REGEX.DECIMAL_2)]],
            addValueSelect: [''],
            addValueValue: [
              '',
              [Validators.max(this.maxValueOfValueField), Validators.pattern(AppConstants.REGEX.INTEGER)],
            ],
          }),
        ]),
        outputValue: ['', [Validators.required, ValidatorHelper.validateOutputValues]],
        master_metric: ['', Validators.required],
        color: [this.defaultZoneColors[0], Validators.required],
      };

      if (this.isGermanFederation) {
        Object.assign(baseZoneConfig, {
          is_double_master_metric: [false],
          master_metric_additional: [''],
          is_speed_toggle: [false],
        });
      }

      this.addForm = this.fb.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
        sport: ['', Validators.required],
        isDefault: [''],
        isPublic: [''],
        zones: this.fb.array([this.fb.group(baseZoneConfig)]),
      });
    }
  }

  private initValue(data: any): void {
    this.addForm.patchValue({ sport: data.sport });
  }

  private initZoneForm(data: TrainingZoneTemplateDto): UntypedFormArray {
    const zones: UntypedFormArray = this.fb.array([]);
    data.zones.forEach((item: ZoneDto, zoneIndex: number): void => {
      const metricItem: number | '' = item.master_metric ? parseInt(item.master_metric) : '';
      this.currentMetric = this.masterMetrics.find((itemFind): boolean => itemFind.value === metricItem);

      if (!this.currentMetric) return;

      const values: UntypedFormArray = this.fb.array([]);
      const currentReferenceSystems: number[] = item.reference_system
        ? item.reference_system.split(',').map(Number)
        : [];
      const isDisableOutput: boolean = this.currentMetric.value === this.constant.MASTER_METRIC.LACTATE_SHUTTLING;
      this.updateAdditionalType(currentReferenceSystems);
      const validator_array: any[] = this.updateValidatorBasedOnMasterMetric();

      item.zone_input_respected_values.forEach((input: any, index: number): void => {
        const addValueSelectOption = this.addValues.find((mm): boolean => mm.value == input.add_value_unit);
        const system = this.getReferenceSystem(currentReferenceSystems[index]);
        values.push(
          this.fb.group({
            key: system ? system.id : 0,
            keyName: system ? system.name : 0,
            targetValue: [{ value: input.target, disabled: isDisableOutput }, validator_array],
            lowerValue: [{ value: input.lower, disabled: isDisableOutput }, validator_array],
            upperValue: [{ value: input.upper, disabled: isDisableOutput }, validator_array],
            addValueSelect: [{ value: addValueSelectOption, disabled: isDisableOutput }],
            addValueValue: [
              { value: input.add_value, disabled: isDisableOutput },
              [Validators.max(this.maxValueOfValueField), Validators.pattern(AppConstants.REGEX.INTEGER)],
            ],
          })
        );
      });

      if (!item.zone_input_respected_values || item.zone_input_respected_values.length === 0) {
        values.push(
          this.fb.group({
            key: 0,
            keyName: 0,
            targetValue: [{ value: '', disabled: true }],
            lowerValue: [{ value: '', disabled: true }],
            upperValue: [{ value: '', disabled: true }],
            addValueSelect: [{ value: '', disabled: true }],
            addValueValue: [
              { value: '', disabled: true },
              [Validators.max(this.maxValueOfValueField), Validators.pattern(AppConstants.REGEX.INTEGER)],
            ],
          })
        );
      }

      const masterMetricOption = this.masterMetrics.find((mm): boolean => mm.value == item.master_metric);
      const masterMetricAdditionalOption = item.master_metric_additional
        ? this.masterMetrics.find((mm): boolean => mm.value.toString() === item.master_metric_additional)
        : null;

      const savedColor = (item as any).color || this.defaultZoneColors[zoneIndex % this.defaultZoneColors.length];
      const baseZoneConfig = {
        name: [item.name, Validators.required],
        code: [item.code, Validators.required],
        referenceSystem: [
          item.reference_system ? item.reference_system.split(',').map(Number) : '',
          [Validators.required, ValidatorHelper.validateSystemRef],
        ],
        values: values,
        outputValue: [
          { value: item.output_value ? item.output_value.split(',').map(Number) : '', disabled: isDisableOutput },
          [Validators.required, ValidatorHelper.validateOutputValues],
        ],
        master_metric: [masterMetricOption || '', Validators.required],
        color: [savedColor, Validators.required],
      };

      if (this.isGermanFederation) {
        Object.assign(baseZoneConfig, {
          is_double_master_metric: [!!item.is_double_master_metric],
          master_metric_additional: [masterMetricAdditionalOption || ''],
          is_speed_toggle: [item.is_speed_toggle],
        });
      }

      zones.push(this.fb.group(baseZoneConfig));
    });
    return zones;
  }

  public onChangeDefault(event: any): void {
    if (event.checked) {
      this.addForm.get('isPublic')?.disable();
      this.addForm.get('isPublic')?.setValue(true);
    } else {
      this.addForm.get('isPublic')?.enable();
    }
  }

  private formatCopyName(name: string): string {
    const copyDepth = this.inferCopyDepth(name);
    const baseName = this.stripCopyPrefix(name);
    const prefix = copyDepth === 0 ? '(COPY)' : copyDepth > 0 ? `(COPY #${copyDepth})` : '';
    return `${prefix} ${baseName}`.trim();
  }

  private stripCopyPrefix(name: string): string {
    return name.replace(/^\(COPY(?:\s#\d+)?\)\s*/, '');
  }

  private inferCopyDepth(name: string): number {
    const match = name.match(/^\(COPY(?:\s#(\d+))?\)/);
    if (!match) return 0;
    return match[1] ? parseInt(match[1]) + 1 : 1;
  }
}
